import { cn } from '@eo/shared';

import { BUDGET_BUCKETS, FORECAST_ROWS, TERM_HELP } from './constants';
import { formatHours } from './helpers';
import type { ForecastBreakdownProps } from './types';

const hoursOrDash = (value: number | null) => (value === null ? '—' : formatHours(value));

export function ForecastBreakdown({ forecast }: ForecastBreakdownProps) {
  return (
    <div className="flex flex-col gap-2">
      <table data-testid="forecast-breakdown" className="w-full text-xs tabular-nums">
        <thead>
          <tr className="text-text-tertiary">
            <th className="py-1 text-left font-normal" />
            {BUDGET_BUCKETS.map(({ key, label }) => (
              <th key={key} className="py-1 text-right font-normal">
                {label}
              </th>
            ))}
            <th className="py-1 text-right font-normal">Total</th>
          </tr>
        </thead>
        <tbody>
          {FORECAST_ROWS.map(({ key, label }) => {
            const emphasised = key === 'forecast' || key === 'projectedOver';
            const fixed = forecast.fixed[key];
            const variable = forecast.variable[key];
            // Projected over adds up per bucket rather than comparing the total
            // forecast with the total budget, so spare fixed never covers variable.
            const total = fixed === null || variable === null ? null : fixed + variable;
            return (
              <tr
                key={key}
                className={cn(
                  'border-t border-border-faint',
                  emphasised ? 'text-text-primary' : 'text-text-secondary',
                )}
              >
                <th scope="row" className="py-1 text-left font-normal">
                  {label}
                </th>
                <td className="py-1 text-right">{hoursOrDash(fixed)}</td>
                <td className="py-1 text-right">{hoursOrDash(variable)}</td>
                <td className="py-1 text-right font-medium">{hoursOrDash(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-xs text-text-tertiary">
        {forecast.estimatedOpenTasks} of {forecast.openTasks} open tasks estimated.
      </p>
      {forecast.fixed.pace === null && (
        <p className="text-xs text-text-tertiary">{TERM_HELP.paceNotYet}</p>
      )}
    </div>
  );
}
